// VideoAdminPanel.jsx - Version corrigée utilisant VideoFirebaseService
import React, { useState, useEffect } from 'react';
import { 
  Loader2, 
  RefreshCw, 
  Trash2, 
  Plus, 
  AlertCircle, 
  CheckCircle, 
  Filter,
  Eye,
  ExternalLink,
  Clock,
  BarChart3,
  TrendingUp,
  Search,
  X,
  Video,
  PlayCircle,
  Star,
  Users,
  Target,
  Download,
  Upload,
  Edit3,
  Brain,
  Layers,
  Bot
} from 'lucide-react';
import videoServices from './VideoService';
import videoFirebaseService from './VideoFirebaseService'; // Utilisation du service unifié

const VideoAdminPanel = ({ userId }) => {
  // États principaux
  const [allVideos, setAllVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedThemesForDiscovery, setSelectedThemesForDiscovery] = useState(['entrepreneurship', 'technology']);
  const [selectedThemesForFilter, setSelectedThemesForFilter] = useState([]);
  const [discovering, setDiscovering] = useState(false);
  const [stats, setStats] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // États pour la recherche
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('relevance');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // États pour la confirmation de suppression
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);

  // États pour la découverte personnalisée
  const [customDiscoveryQuery, setCustomDiscoveryQuery] = useState('');
  const [showCustomDiscovery, setShowCustomDiscovery] = useState(false);

  // Thèmes disponibles avec design harmonisé
  const availableThemes = [
    { id: 'entrepreneurship', label: 'Entrepreneuriat', color: 'bg-blue-500', icon: '🚀', textColor: 'text-blue-800', bgLight: 'bg-blue-50' },
    { id: 'technology', label: 'Technologie', color: 'bg-emerald-500', icon: '💻', textColor: 'text-emerald-800', bgLight: 'bg-emerald-50' },
    { id: 'finance', label: 'Finance', color: 'bg-amber-500', icon: '💰', textColor: 'text-amber-800', bgLight: 'bg-amber-50' },
    { id: 'leadership', label: 'Leadership', color: 'bg-purple-500', icon: '👑', textColor: 'text-purple-800', bgLight: 'bg-purple-50' },
    { id: 'health', label: 'Santé & Bien-être', color: 'bg-green-500', icon: '🌱', textColor: 'text-green-800', bgLight: 'bg-green-50' },
    { id: 'creativity', label: 'Créativité', color: 'bg-pink-500', icon: '🎨', textColor: 'text-pink-800', bgLight: 'bg-pink-50' }
  ];

  useEffect(() => {
    loadAdminData();
  }, [userId]);

  // Effect pour filtrer et trier les vidéos
  useEffect(() => {
    applyFiltersAndSort();
  }, [allVideos, selectedThemesForFilter, searchQuery, sortBy, sortOrder]);

  // ================================
  // CHARGEMENT DES DONNÉES
  // ================================

  const loadAdminData = async () => {
    if (!userId) {
      setError('Erreur : Vous devez être connecté pour accéder au panneau d\'administration.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('🎥 VideoAdminPanel - Chargement des données admin vidéos...');
      
      // Charger toutes les vidéos via VideoFirebaseService
      const videos = await videoFirebaseService.getAllVideos(200, userId);
      setAllVideos(videos);
      console.log(`✅ ${videos.length} vidéos chargées`);

      // Charger les statistiques via VideoFirebaseService
      try {
        const statistics = await videoFirebaseService.getVideoStats();
        setStats(statistics);
        console.log('✅ Statistiques vidéo chargées:', statistics);
      } catch (statsError) {
        console.warn('Erreur chargement statistiques vidéo:', statsError);
        setStats({
          totalVideos: videos.length,
          totalViews: videos.reduce((sum, v) => sum + (v.totalViews || v.viewCount || 0), 0),
          byTheme: {},
          byDiscoveryMethod: { backend: 0, manual: 0, youtube: 0 },
          recentlyAdded: 0,
          averagePopularity: 0
        });
      }

    } catch (error) {
      console.error('❌ Erreur chargement admin vidéos:', error);
      setError('Erreur lors du chargement des données d\'administration des vidéos.');
    } finally {
      setLoading(false);
    }
  };

  // ================================
  // FILTRAGE ET TRI
  // ================================

  const applyFiltersAndSort = () => {
    let filtered = [...allVideos];

    // Filtrage par thèmes
    if (selectedThemesForFilter.length > 0) {
      filtered = filtered.filter(video => 
        selectedThemesForFilter.includes(video.theme)
      );
    }

    // Filtrage par recherche
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(video => 
        video.title?.toLowerCase().includes(query) ||
        video.description?.toLowerCase().includes(query) ||
        video.channelTitle?.toLowerCase().includes(query) ||
        video.summary?.toLowerCase().includes(query)
      );
    }

    // Tri
    filtered.sort((a, b) => {
      let valueA, valueB;
      
      switch (sortBy) {
        case 'popularity':
          valueA = a.viewCount || 0;
          valueB = b.viewCount || 0;
          break;
        case 'quality':
          valueA = a.popularityScore || a.qualityRating || 0;
          valueB = b.popularityScore || b.qualityRating || 0;
          break;
        case 'recent':
          valueA = new Date(a.addedAt || 0);
          valueB = new Date(b.addedAt || 0);
          break;
        case 'relevance':
        default:
          valueA = a.relevanceScore || a.popularityScore || 0;
          valueB = b.relevanceScore || b.popularityScore || 0;
          break;
      }

      if (sortOrder === 'desc') {
        return valueB > valueA ? 1 : -1;
      } else {
        return valueA > valueB ? 1 : -1;
      }
    });

    setFilteredVideos(filtered);
  };

  const handleFilterToggle = (themeId) => {
    setSelectedThemesForFilter(prev => {
      if (prev.includes(themeId)) {
        return prev.filter(id => id !== themeId);
      } else {
        return [...prev, themeId];
      }
    });
  };

  const clearFilters = () => {
    setSelectedThemesForFilter([]);
    setSearchQuery('');
  };

  // ================================
  // DÉCOUVERTE DE VIDÉOS avec VideoFirebaseService
  // ================================

  const handleDiscoverVideosByThemes = async () => {
    if (!userId) {
      setError('Vous devez être connecté pour découvrir des vidéos.');
      return;
    }

    if (selectedThemesForDiscovery.length === 0) {
      setError('Veuillez sélectionner au moins un thème pour la découverte.');
      return;
    }

    setDiscovering(true);
    setError('');
    setSuccessMessage('');

    try {
      console.log('🔄 VideoAdminPanel - Découverte vidéos pour les thèmes:', selectedThemesForDiscovery);
      
      const results = {};
      let totalAdded = 0;
      let totalDiscovered = 0;
      
      for (const theme of selectedThemesForDiscovery) {
        try {
          console.log(`🎥 Découverte vidéos pour ${theme} avec VideoFirebaseService...`);
          
          // Utiliser VideoFirebaseService pour la découverte
          const result = await videoFirebaseService.discoverAndAddVideos(theme, userId, {
            maxVideos: 15,
            forceRefresh: true
          });
          
          results[theme] = {
            discovered: result.discovered,
            added: result.added,
            skipped: result.skipped,
            errors: result.errors || []
          };
          
          totalAdded += result.added;
          totalDiscovered += result.discovered;
          
          console.log(`✅ Thème ${theme}: ${result.added} ajoutées / ${result.discovered} découvertes`);
          
          // Pause entre les thèmes pour éviter la surcharge
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (themeError) {
          console.error(`❌ Erreur pour ${theme}:`, themeError);
          results[theme] = { 
            discovered: 0, 
            added: 0, 
            skipped: 0,
            error: themeError.message 
          };
        }
      }

      // Recharger les données après découverte
      try {
        await loadAdminData();
      } catch (reloadError) {
        console.warn('Erreur rechargement:', reloadError);
      }

      // Message de succès détaillé
      if (totalAdded > 0) {
        setSuccessMessage(`✅ ${totalAdded} nouvelles vidéos ajoutées sur ${totalDiscovered} découvertes ! Détails: ${Object.entries(results).map(([theme, result]) => `${theme}: +${result.added}`).join(', ')}`);
      } else if (totalDiscovered > 0) {
        setSuccessMessage(`ℹ️ ${totalDiscovered} vidéos découvertes (doublons évités). Vérifiez la configuration backend.`);
      } else {
        setSuccessMessage('ℹ️ Découverte terminée (aucune nouvelle vidéo trouvée). Vérifiez la connexion backend.');
      }

      console.log('✅ VideoAdminPanel - Découverte vidéos terminée:', {
        totalDiscovered,
        totalAdded,
        detailedResults: results
      });

    } catch (error) {
      console.error('❌ Erreur découverte vidéos:', error);
      setError(`Erreur lors de la découverte: ${error.message}`);
    } finally {
      setDiscovering(false);
    }
  };

  const handleCustomDiscovery = async () => {
    if (!customDiscoveryQuery.trim()) {
      setError('Veuillez saisir une requête de recherche.');
      return;
    }

    setDiscovering(true);
    setError('');
    setSuccessMessage('');

    try {
      console.log('🔍 VideoAdminPanel - Découverte personnalisée:', customDiscoveryQuery);
      
      // Déterminer le thème principal basé sur la requête
      const detectedTheme = detectThemeFromQuery(customDiscoveryQuery);
      
      // Utiliser VideoFirebaseService pour la recherche personnalisée
      const result = await videoFirebaseService.discoverVideosByQuery(
        customDiscoveryQuery, 
        detectedTheme, 
        userId, 
        { maxVideos: 20 }
      );
      
      // Recharger les données
      await loadAdminData();
      
      if (result.added > 0) {
        setSuccessMessage(`✅ ${result.added} vidéos ajoutées pour "${customDiscoveryQuery}" (${result.discovered} découvertes)`);
      } else {
        setSuccessMessage(`ℹ️ ${result.discovered} vidéos découvertes pour "${customDiscoveryQuery}" (doublons évités)`);
      }
      
      setCustomDiscoveryQuery('');
      setShowCustomDiscovery(false);
      
    } catch (error) {
      console.error('❌ Erreur découverte personnalisée:', error);
      setError(`Erreur lors de la découverte personnalisée: ${error.message}`);
    } finally {
      setDiscovering(false);
    }
  };

  const detectThemeFromQuery = (query) => {
    const queryLower = query.toLowerCase();
    
    for (const theme of availableThemes) {
      if (queryLower.includes(theme.label.toLowerCase()) || 
          queryLower.includes(theme.id)) {
        return theme.id;
      }
    }
    
    // Mots-clés pour détecter les thèmes
    if (queryLower.includes('startup') || queryLower.includes('business') || queryLower.includes('entrepreneur')) {
      return 'entrepreneurship';
    }
    if (queryLower.includes('tech') || queryLower.includes('programmation') || queryLower.includes('ia')) {
      return 'technology';
    }
    if (queryLower.includes('finance') || queryLower.includes('investissement') || queryLower.includes('bourse')) {
      return 'finance';
    }
    if (queryLower.includes('leadership') || queryLower.includes('management') || queryLower.includes('équipe')) {
      return 'leadership';
    }
    if (queryLower.includes('santé') || queryLower.includes('bien-être') || queryLower.includes('nutrition')) {
      return 'health';
    }
    if (queryLower.includes('créativité') || queryLower.includes('design') || queryLower.includes('art')) {
      return 'creativity';
    }
    
    return 'entrepreneurship'; // thème par défaut
  };

  const handleDiscoveryThemeChange = (themeId) => {
    setSelectedThemesForDiscovery(prev => {
      if (prev.includes(themeId)) {
        return prev.filter(id => id !== themeId);
      } else {
        return [...prev, themeId];
      }
    });
  };

  // ================================
  // GESTION DES VIDÉOS
  // ================================

  const handleDeleteVideo = async (videoId) => {
    if (!userId) {
      setError('Vous devez être connecté pour supprimer une vidéo.');
      return;
    }

    setVideoToDelete(videoId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!videoToDelete) return;

    try {
      // Utiliser VideoFirebaseService pour la suppression
      await videoFirebaseService.deleteVideo(videoToDelete, userId);
      
      // Mettre à jour les listes localement
      setAllVideos(prev => prev.filter(video => video.id !== videoToDelete));
      
      // Recharger les stats
      try {
        const updatedStats = await videoFirebaseService.getVideoStats();
        setStats(updatedStats);
      } catch (statsError) {
        console.warn('Erreur rechargement stats après suppression:', statsError);
      }

      setSuccessMessage('✅ Vidéo supprimée avec succès');

    } catch (error) {
      console.error('❌ Erreur suppression:', error);
      setError(`Erreur lors de la suppression: ${error.message}`);
    } finally {
      setShowDeleteConfirm(false);
      setVideoToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setVideoToDelete(null);
  };

  // ================================
  // UTILITAIRES
  // ================================

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'À l\'instant';
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    return `Il y a ${Math.floor(diffHours / 24)}j`;
  };

  const formatViewCount = (viewCount) => {
    if (!viewCount) return '0';
    if (viewCount >= 1000000) return `${(viewCount / 1000000).toFixed(1)}M`;
    if (viewCount >= 1000) return `${(viewCount / 1000).toFixed(1)}K`;
    return viewCount.toLocaleString();
  };

  // Fonction pour effacer les messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // ================================
  // RENDU
  // ================================

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        <p className="mt-4 text-gray-600">Chargement du panneau d'administration des vidéos...</p>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto w-full p-6">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
          <Layers className="h-8 w-8 text-blue-600" />
          Gestion des Contenus Vidéo
        </h1>
        <button
          onClick={loadAdminData}
          className="flex items-center gap-2 text-gray-600 hover:text-blue-600 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Actualiser tout
        </button>
      </div>

      {/* Messages */}
      {successMessage && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <p className="text-green-700">{successMessage}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      )}

      {/* Statistiques */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600 rounded-xl">
                <Video className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-blue-800">Total Vidéos</h3>
                <p className="text-2xl font-bold text-blue-600">{stats.totalVideos}</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border border-emerald-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-600 rounded-xl">
                <Eye className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-emerald-800">Vues Totales</h3>
                <p className="text-2xl font-bold text-emerald-600">{formatViewCount(stats.totalViews)}</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-600 rounded-xl">
                <Brain className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-purple-800">Découvertes</h3>
                <p className="text-2xl font-bold text-purple-600">{stats.discoveryStats?.backendVideos || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 border border-amber-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-600 rounded-xl">
                <Star className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-amber-800">Qualité Moyenne</h3>
                <p className="text-2xl font-bold text-amber-600">{stats.averagePopularity}/10</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section Découverte */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Bot className="h-5 w-5 text-blue-600" />
          Découvrir de Nouvelles Vidéos d'Inspiration
        </h2>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Sélectionnez les thèmes pour la découverte :
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {availableThemes.map(theme => (
              <label key={theme.id} className={`flex items-center space-x-3 p-3 rounded-xl border hover:bg-gray-50 cursor-pointer transition-colors ${theme.bgLight}`}>
                <input
                  type="checkbox"
                  checked={selectedThemesForDiscovery.includes(theme.id)}
                  onChange={() => handleDiscoveryThemeChange(theme.id)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-lg">{theme.icon}</span>
                <span className={`text-sm font-medium ${theme.textColor}`}>{theme.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex gap-3 mb-4">
          <button
            onClick={handleDiscoverVideosByThemes}
            disabled={discovering || selectedThemesForDiscovery.length === 0}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl transition-all duration-200 shadow-sm ${
              discovering || selectedThemesForDiscovery.length === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {discovering ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Découverte en cours...</span>
              </>
            ) : (
              <>
                <Download className="h-5 w-5" />
                <span>Découvrir par Thèmes</span>
              </>
            )}
          </button>

          <button
            onClick={() => setShowCustomDiscovery(!showCustomDiscovery)}
            className="flex items-center gap-2 px-6 py-3 border border-blue-600 text-blue-600 rounded-xl hover:bg-blue-50 transition-colors shadow-sm"
          >
            <Search className="h-5 w-5" />
            <span>Découverte Personnalisée</span>
          </button>
        </div>

        {/* Découverte personnalisée */}
        {showCustomDiscovery && (
          <div className="bg-blue-50 rounded-xl p-4 mt-4 border border-blue-200">
            <label className="block text-sm font-medium text-blue-800 mb-2">
              Recherche personnalisée :
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={customDiscoveryQuery}
                onChange={(e) => setCustomDiscoveryQuery(e.target.value)}
                placeholder="Ex: coaching entrepreneurial, développement web, yoga débutant..."
                className="flex-1 px-3 py-2 border border-blue-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && handleCustomDiscovery()}
              />
              <button
                onClick={handleCustomDiscovery}
                disabled={discovering || !customDiscoveryQuery.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:bg-gray-300 transition-colors shadow-sm"
              >
                {discovering ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Rechercher'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Section Filtres et Recherche */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <Filter className="h-5 w-5 text-blue-600" />
            Filtrer et Rechercher
          </h2>
          <div className="text-sm text-gray-500">
            {filteredVideos.length} / {allVideos.length} vidéos affichées
          </div>
        </div>
        
        {/* Barre de recherche */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher par titre, description, chaîne..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Filtres par thème */}
        <div className="flex flex-wrap gap-3 mb-4">
          {availableThemes.map(theme => {
            const count = allVideos.filter(video => video.theme === theme.id).length;
            const isSelected = selectedThemesForFilter.includes(theme.id);
            
            return (
              <button
                key={theme.id}
                onClick={() => handleFilterToggle(theme.id)}
                className={`px-3 py-2 rounded-xl text-sm font-medium transition-colors border flex items-center gap-2 shadow-sm ${
                  isSelected
                    ? `${theme.bgLight} ${theme.textColor} border-${theme.color.split('-')[1]}-300`
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                }`}
              >
                <span>{theme.icon}</span>
                <span>{theme.label}</span>
                <span className="bg-white rounded-full px-2 py-0.5 text-xs">
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Options de tri */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-700">Trier par :</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1 text-sm"
            >
              <option value="relevance">Pertinence</option>
              <option value="popularity">Popularité (vues)</option>
              <option value="quality">Score de qualité</option>
              <option value="recent">Plus récentes</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {sortOrder === 'desc' ? '↓ Décroissant' : '↑ Croissant'}
            </button>
          </div>

          <button
            onClick={clearFilters}
            className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
          >
            Effacer tous les filtres
          </button>
        </div>
      </div>

      {/* Liste des vidéos */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800 flex items-center justify-between">
            <span>
              Vidéos d'Inspiration ({filteredVideos.length})
              {selectedThemesForFilter.length > 0 && (
                <span className="text-sm text-blue-600 ml-2 font-normal">
                  (Filtrées par: {selectedThemesForFilter.map(id => 
                    availableThemes.find(t => t.id === id)?.label
                  ).join(', ')})
                </span>
              )}
            </span>
          </h2>
        </div>

        <div className="divide-y divide-gray-200">
          {filteredVideos.length > 0 ? (
            filteredVideos.map((video) => (
              <div 
                key={`video-admin-${video.id}`}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  {/* Thumbnail */}
                  <div className="relative flex-shrink-0">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-32 h-20 object-cover rounded-lg"
                      onError={(e) => {
                        const seed = video.id ? Math.abs(video.id.split('').reduce((a, b) => a + b.charCodeAt(0), 0)) : Math.floor(Math.random() * 1000);
                        e.target.src = `https://picsum.photos/320/180?random=${seed}`;
                      }}
                    />
                    <div className="absolute bottom-1 right-1 bg-black/80 text-white text-xs px-1 py-0.5 rounded">
                      {video.duration}
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                      {video.title}
                    </h3>
                    
                    <div className="flex items-center gap-3 text-sm text-gray-500 mb-2">
                      <span className={`capitalize px-2 py-1 rounded-lg text-white text-xs font-medium ${
                        availableThemes.find(t => t.id === video.theme)?.color || 'bg-gray-500'
                      }`}>
                        {availableThemes.find(t => t.id === video.theme)?.icon} {availableThemes.find(t => t.id === video.theme)?.label || video.theme}
                      </span>
                      <span className="font-medium">{video.channelTitle}</span>
                      <span>•</span>
                      <span>{getTimeAgo(video.addedAt)}</span>
                      {video.viewCount > 0 && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {formatViewCount(video.viewCount)} vues
                          </span>
                        </>
                      )}
                      {(video.popularityScore || video.qualityRating) && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Star className="h-3 w-3" />
                            {(video.popularityScore || video.qualityRating).toFixed(1)}/10
                          </span>
                        </>
                      )}
                    </div>
                    
                    {(video.description || video.summary) && (
                      <p className="text-gray-600 line-clamp-2 leading-relaxed">
                        {video.summary || video.description}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {video.url && video.url !== '#' && (
                      <a
                        href={video.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm px-3 py-2 rounded-lg hover:bg-blue-50 flex items-center gap-1 transition-colors"
                      >
                        <PlayCircle className="h-4 w-4" />
                        Voir
                      </a>
                    )}
                    <button
                      onClick={() => handleDeleteVideo(video.id)}
                      className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-12 text-center">
              <div className="text-6xl mb-4">🎥</div>
              <p className="text-gray-500 mb-4">
                {selectedThemesForFilter.length > 0 || searchQuery
                  ? `Aucune vidéo trouvée pour les filtres sélectionnés.`
                  : `Aucune vidéo d'inspiration disponible.`
                }
              </p>
              {selectedThemesForFilter.length > 0 || searchQuery ? (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-sm"
                >
                  Effacer les filtres
                </button>
              ) : (
                <button
                  onClick={handleDiscoverVideosByThemes}
                  className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-sm"
                >
                  Découvrir des Vidéos
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full m-4 border border-gray-200">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Confirmer la suppression
              </h3>
              <p className="text-gray-600 mb-6">
                Êtes-vous sûr de vouloir supprimer cette vidéo ? Cette action est irréversible.
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors shadow-sm"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Styles CSS */}
      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default VideoAdminPanel;